import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'

import { makeServerProps } from '@/server/query'

import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import makePage from '@/client/services/pages/makePage'
import { getCertificationUrl, getPartnershipsUrl, getPublicUrl } from '@/client/services/url'

import FAQ from '@/components/FAQ/FAQ'
import Section from '@/components/Landing/Section'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import TutorCertificationCard from '@/components/TutorCertificationCard'

const FAQ_ITEMS = [
  {
    question: 'How do I become a certified tutor?',
    answer: () => (
      <Text>
        Different organizations have different requirements for becoming a certified tutor. At
        Schoolhouse.world, we allow you to tutor{' '}
        <ShwLink href="https://schoolhouse.world/blog/free-online-math-tutoring-sessions/">
          math
        </ShwLink>{' '}
        and science subjects by demonstrating your knowledge. This process involves video-recording
        yourselves taking a cumulative or unit-based assessment directly on{' '}
        <ShwLink href="https://www.khanacademy.org/">Khan Academy</ShwLink>, as you explain your
        reasoning.
        <p>
          After submitting your video, you&apos;ll peer-review two videos from other students who
          are trying to get certified in the same unit. If approved, you&apos;ll then receive
          training before hosting your first tutoring session.
        </p>
        To tutor topics in SAT®, we currently offer Honorary Certifications through a form-based
        application process where you state any relevant qualifications (such as your SAT score!).
        These requests are reviewed by a small team of trusted volunteers.
      </Text>
    ),
  },
  {
    question: 'What qualifications do I need to become a tutor?',
    answer: () => (
      <Text>
        We believe that everyone can teach others what they know. Schoolhouse.world does not require
        specific qualifications besides demonstrated knowledge in your subject area of
        choice–whether that is through video-based certifications, or Honorary Certifications. After
        getting certified, you&apos;ll then receive training that prepares you to host your first
        tutoring session—you can either host one-off tutoring sessions.
      </Text>
    ),
  },
  {
    question: 'What else are certifications good for?',
    answer: () => (
      <Text>
        Besides letting you tutor, our certifications also allow you to showcase your mastery in
        math and science. Your certifications will go in a transcript, which can be shared online
        through a convenient link. This transcript is a great way to demonstrate academic
        achievement, especially to colleges! You can view our list of current college partners{' '}
        <ShwLink href={getPartnershipsUrl()}>here</ShwLink>.
      </Text>
    ),
  },
  {
    question: 'Can I be a tutor without a degree?',
    answer: () => (
      <Text>
        At Schoolhouse.world, no degree is required for becoming a certified tutor! In fact, we
        pride ourselves on being an alternative model for education. Many of our tutors are
        high-school age students themselves, and we believe anyone can tutor as long as they&apos;re
        excited to learn and help fellow{' '}
        <ShwLink href="https://schoolhouse.world/blog/the-power-of-peer-tutoring/">peers</ShwLink>.
      </Text>
    ),
  },
  {
    question: 'Is it hard to be a tutor?',
    answer: () => (
      <Text>
        We believe in lowering the barrier to{' '}
        <ShwLink href="https://schoolhouse.world/blog/benefits-of-becoming-a-tutor/">
          being a tutor
        </ShwLink>{' '}
        as much as possible, while ensuring a quality experience for our learners. Tutoring can be
        as easy as hosting a 30-minute{' '}
        <ShwLink href="https://schoolhouse.world/blog/benefits-of-group-learning/">
          group session
        </ShwLink>
        ! We&apos;ll provide training and mentorship to make your tutoring journey as smooth as
        possible.
      </Text>
    ),
  },
  {
    question: 'Can a 16-year-old become a tutor?',
    answer: () => (
      <Text>
        Yes! We firmly believe students can be effective tutors. For legal reasons, you must be at
        least 13 years old to learn and tutor on Schoolhouse.world.
      </Text>
    ),
  },
]

function TutoringCertification(): ReactElement {
  const query = useQuery()
  const featuredTutors = query.many(UserQ.getFeaturedCertifiedTutors())

  return (
    <Layout
      title="Tutor Certification: Become a Certified Tutor for Schoolhouse"
      ignoreTitleSuffix
      meta={{
        description:
          'Get certified as a math tutor on Schoolhouse.world. Demonstrate your skills, unlock exclusive opportunities, and build a standout portfolio.',
      }}
    >
      <Box
        overflow="hidden"
        position="relative"
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        alignItems="center"
        bgImage={`url(${getPublicUrl()}/certification/certification-bg.png)`}
        backgroundSize="cover"
        pt="30px"
        pb="50px"
        width="100%"
      >
        <Container centerContent>
          <Stack direction="row" spacing={['0px', '0px', '48px']} align="center" maxWidth="800px">
            <PublicImage
              width="180px"
              display={['none', 'none', 'inline']}
              src="/certification/certification-bg-transcript.png"
            />
            <Box alignItems="left">
              <Heading size="3xl" as="h1">
                Become a Certified Tutor
              </Heading>
              <Text my="16px">
                Become a certified tutor at Schoolhouse to showcase your mastery in core topics.
                Build a portfolio that shows what you&apos;re ready to teach!
              </Text>
              <ShwLink href={getCertificationUrl('get-certified')}>
                <Button variant="outline" width={['100%', 'auto']}>
                  Get Certified
                </Button>
              </ShwLink>
            </Box>
          </Stack>
        </Container>
      </Box>
      <Container centerContent mb="64px">
        <Box margin="auto" maxWidth="1240px" width="100%">
          <Stack maxWidth="750px" spacing="64px" align="center" margin="auto">
            <Heading as="h2" size="2xl" mt="64px">
              How it works
            </Heading>
            <Stack direction={['column', 'row']} spacing="48px" width="100%">
              <Center>
                <PublicImage width="180px" src="/certification/unit_icon.png" />
              </Center>
              <Center>
                <Box>
                  <Heading as="h4" size="lg" mb="16px">
                    1. SELECT A UNIT
                  </Heading>
                  <Text>Choose a Khan Academy unit to become a certified tutor in.</Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="48px" width="100%">
              <Center>
                <PublicImage width="180px" src="/certification/video_icon.png" />
              </Center>
              <Center>
                <Box>
                  <Heading as="h4" size="lg" mb="16px">
                    2. RECORD A VIDEO
                  </Heading>
                  <Text>
                    Achieve at least a 90% on the unit test while explaining your reasoning aloud.
                    You will record a video of your screen and webcam to verify that it was you who
                    did the work, and submit this for review. You are allowed to take the test as
                    many times as you want until you master it.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="48px" width="100%">
              <Center>
                <PublicImage width="180px" src="/certification/peer_review_icon.png" />
              </Center>
              <Center>
                <Box>
                  <Heading as="h4" size="lg" mb="16px">
                    3. HELP OTHERS GET APPROVED
                  </Heading>
                  <Text>
                    Peer review two videos from students around the world who are trying to get
                    tutor certification in the same unit.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="48px" width="100%">
              <Center>
                <PublicImage width="180px" src="/certification/certification_icon.png" />
              </Center>
              <Center>
                <Box>
                  <Heading as="h4" size="lg" mb="16px">
                    4. GET YOUR CERTIFICATION
                  </Heading>
                  <Text>
                    That’s all! Once your video has been reviewed, you’ll get an email with a
                    publicly shareable link to your Schoolhouse.world tutor certification
                    transcript.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Flex align="center">
              <ShwLink href={getCertificationUrl('get-certified')}>
                <Button variant="outline" width={['100%', 'auto']}>
                  Get Certified
                </Button>
              </ShwLink>
            </Flex>
          </Stack>
          <Stack direction="column" align="center" maxWidth="784px" margin="auto" spacing="20px">
            <Heading as="h2" size="2xl" mt="64px" textAlign="center">
              See Schoolhouse certifications in action
            </Heading>
            <Text textAlign="center">
              We’ll display your certifications in a Schoolhouse portfolio that showcases what you
              know–and highlights any tutoring experience with us! Check out some portfolios from
              some awesome{' '}
              <ShwLink href="https://schoolhouse.world/blog/volunteer-tutoring-at-schoolhouse/">
                volunteer tutors
              </ShwLink>{' '}
              in our community.
            </Text>
          </Stack>
          <Stack direction="column" align="center" maxWidth="1240px" margin="auto" mt="46px">
            <SimpleGrid columns={[1, 2, 3]} width="100%" spacing={['24px', '28px', '32px']}>
              {featuredTutors.map(tutor => (
                <TutorCertificationCard tutor={tutor} key={tutor(UserQ.id) ?? 0} />
              ))}
            </SimpleGrid>
          </Stack>
          <Section maxWidth="700px">
            <FAQ
              items={FAQ_ITEMS}
              category="certification"
              headerText=" What it Takes to Become a Certified Tutor"
            />
          </Section>
        </Box>
      </Container>
      <Box width="100%" bg="bg.info" py="32px">
        <Container centerContent>
          <Box maxWidth="1000px" textAlign="center">
            <Heading size="3xl" as="h2" mb="32px" fontSize="32px">
              Test your skills and teach what you know. Get certified today!
            </Heading>
            <ShwLink href={getCertificationUrl('get-certified')}>
              <Button variant="solid" width={['100%', 'auto']}>
                Get Certified
              </Button>
            </ShwLink>
          </Box>
        </Container>
      </Box>
    </Layout>
  )
}

const TutoringCertificationPage = makePage(TutoringCertification, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TutoringCertificationPage)
export default TutoringCertificationPage
